/**
 * R-64 — embedding cosine-similarity topical drift detector.
 */
import { cosineSimilarity } from '../embeddings/cosine';
import precomputedEmbeddings from './sectionDriftEmbeddings.json';
import type { SectionDriftRegistryEntry } from './sectionDriftRegistry';
import { registryKey } from './sectionDriftRegistry';

export type EmbedFn = (text: string) => Promise<number[]>;

export interface EmbeddingDriftFinding {
  detector: 'embedding';
  reportTypeId: string;
  sectionId: string;
  severity: 'block' | 'warn';
  similarity: number;
  message: string;
}

const sectionEmbeddingCache = new Map<string, number[]>();
const contentHashCache = new Map<string, number[]>();

function simpleHash(text: string): string {
  let h = 0;
  for (let i = 0; i < text.length; i += 1) {
    h = ((h << 5) - h + text.charCodeAt(i)) | 0;
  }
  return `h${h}:${text.length}`;
}

function loadPrecomputedReferenceVector(entry: SectionDriftRegistryEntry): number[] | undefined {
  const key = registryKey(entry.reportTypeId, entry.sectionId);
  const row = (precomputedEmbeddings as Record<string, number[]>)[key];
  if (row && row.length > 0) return row;
  const byRef = (precomputedEmbeddings as Record<string, number[]>)[entry.embeddingReference];
  if (byRef && byRef.length > 0) return byRef;
  return undefined;
}

async function resolveReferenceVector(
  entry: SectionDriftRegistryEntry,
  embedFn: EmbedFn,
): Promise<number[]> {
  const cacheKey = registryKey(entry.reportTypeId, entry.sectionId);
  const cached = sectionEmbeddingCache.get(cacheKey);
  if (cached?.length) return cached;

  const precomputed = loadPrecomputedReferenceVector(entry);
  if (precomputed?.length) {
    sectionEmbeddingCache.set(cacheKey, precomputed);
    return precomputed;
  }

  const embedded = await embedFn(entry.embeddingReference);
  if (embedded.length > 0) sectionEmbeddingCache.set(cacheKey, embedded);
  return embedded;
}

export async function detectEmbeddingDrift(
  sectionText: string,
  registryEntry: SectionDriftRegistryEntry,
  embedFn: EmbedFn,
): Promise<EmbeddingDriftFinding[]> {
  const text = sectionText.trim();
  if (!text || text.length < 40) return [];

  const contentHash = simpleHash(text);
  let sectionVector = contentHashCache.get(contentHash);
  if (!sectionVector) {
    sectionVector = await embedFn(text);
    if (sectionVector.length > 0) contentHashCache.set(contentHash, sectionVector);
  }
  if (!sectionVector.length) return [];

  const referenceVector = await resolveReferenceVector(registryEntry, embedFn);
  if (!referenceVector.length) return [];

  const similarity = cosineSimilarity(sectionVector, referenceVector);

  let severity: 'block' | 'warn' | null = null;
  if (similarity < 0.30) severity = 'block';
  else if (similarity < 0.50) severity = 'warn';
  if (!severity) return [];

  return [{
    detector: 'embedding',
    reportTypeId: registryEntry.reportTypeId,
    sectionId: registryEntry.sectionId,
    severity,
    similarity,
    message: `Section "${registryEntry.sectionId}" embedding similarity ${similarity.toFixed(2)} (threshold ${severity === 'block' ? '<0.30' : '<0.50'}).`,
  }];
}

/** Test helper — reset in-memory caches between cases. */
export function resetEmbeddingDriftCaches(): void {
  sectionEmbeddingCache.clear();
  contentHashCache.clear();
}
